import { signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { untilDestroyed } from '@ngneat/until-destroy';
import { MenuItemType } from 'mbs-ui-shared@components/app-header/app-header.types';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';

export abstract class AppHeaderBaseComponent {
  public mainMenu: Array<MenuItemType> = [];

  protected abstract router: Router;
  protected activatedUrl = signal<string>(null);

  protected filterCanMenus(menus: MenuItemType[]) {
    return menus?.filter((m) => m.can);
  }

  protected initStreams(): void {
    this.activatedUrl.set(this.normalizeUrl(this.router.url));

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map((event) => this.normalizeUrl(event?.url)),
        distinctUntilChanged(),
        untilDestroyed(this)
      )
      .subscribe((url) => this.activatedUrl.set(url));
  }

  protected isActiveMenuItem(menuItem: MenuItemType): boolean {
    const result = !!menuItem && this.isActiveRoute(menuItem.routeUrl || menuItem.externalUrl);

    if (result) {
      return true;
    }

    if (menuItem?.additionalRouteUrls?.length) {
      return menuItem.additionalRouteUrls.some((url) => this.isActiveRoute(url));
    }

    return result;
  }

  protected getActiveChild(menuItem: MenuItemType): MenuItemType {
    const getActiveSubMenuItem = (subMenuItems: MenuItemType[]): MenuItemType => {
      if (subMenuItems) {
        for (let i = 0; i < subMenuItems.length; i++) {
          const child = subMenuItems[i];

          if (child?.children?.length) {
            const found = getActiveSubMenuItem(subMenuItems[i].children);

            if (found) return found;
          } else if (!child.action && !child.isHorizontalDivider && this.isActiveMenuItem(child)) {
            return child;
          }
        }
      }

      return undefined;
    };

    return getActiveSubMenuItem(menuItem?.children);
  }

  private normalizeUrl(url: string): string {
    return (url || '').split('?')[0];
  }

  protected isActiveRoute(route: string): boolean {
    const activatedUrl = this.activatedUrl();

    if (!activatedUrl || !route) {
      return false;
    }

    return activatedUrl === route || activatedUrl.startsWith(route + '/');
  }
}
